import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { API, clearAdminToken, getAdminHeaders } from '../lib/adminAuth';

const emptyPortfolio = {
  title_id: '',
  title_en: '',
  description_id: '',
  description_en: '',
  category: '',
  image_url: '',
  client: '',
  year: new Date().getFullYear(),
  technologies: '',
};

const emptyBlog = {
  title_id: '',
  title_en: '',
  slug: '',
  excerpt_id: '',
  excerpt_en: '',
  content_id: '',
  content_en: '',
  category: '',
  image_url: '',
  author: 'Admin',
  published: true,
};

const emptyMenu = {
  label_id: '',
  label_en: '',
  path: '',
  order: 0,
  is_public: 1,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('portfolio');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [portfolios, setPortfolios] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [menus, setMenus] = useState([]);

  const [editingPortfolioId, setEditingPortfolioId] = useState(null);
  const [portfolioForm, setPortfolioForm] = useState(emptyPortfolio);

  const [editingBlogId, setEditingBlogId] = useState(null);
  const [blogForm, setBlogForm] = useState(emptyBlog);

  const [editingMenuId, setEditingMenuId] = useState(null);
  const [menuForm, setMenuForm] = useState(emptyMenu);

  const authHeaders = useMemo(() => getAdminHeaders(), []);

  const normalizePortfolio = (item) => ({
    ...item,
    technologies: Array.isArray(item.technologies)
      ? item.technologies.join(', ')
      : item.technologies || '',
  });

  const fetchAll = async () => {
    setLoading(true);
    setError('');

    try {
      const [portfolioRes, blogRes, menuRes] = await Promise.all([
        axios.get(`${API}/portfolio.php`),
        axios.get(`${API}/blog.php?all=1`, { headers: authHeaders }),
        axios.get(`${API}/menus.php`),
      ]);

      setPortfolios(portfolioRes.data);
      setBlogs(blogRes.data);
      setMenus(menuRes.data.data || menuRes.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Gagal memuat data admin.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const logout = () => {
    clearAdminToken();
    navigate('/admin/login', { replace: true });
  };

  const submitPortfolio = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...portfolioForm,
        year: Number(portfolioForm.year),
        technologies: portfolioForm.technologies,
      };

      if (editingPortfolioId) {
        await axios.put(`${API}/portfolio.php?id=${editingPortfolioId}`, payload, { headers: authHeaders });
      } else {
        await axios.post(`${API}/portfolio.php`, payload, { headers: authHeaders });
      }

      setPortfolioForm(emptyPortfolio);
      setEditingPortfolioId(null);
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.error || 'Gagal menyimpan portofolio.');
    } finally {
      setSaving(false);
    }
  };

  const submitBlog = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (editingBlogId) {
        await axios.put(`${API}/blog.php?id=${editingBlogId}`, blogForm, { headers: authHeaders });
      } else {
        await axios.post(`${API}/blog.php`, blogForm, { headers: authHeaders });
      }

      setBlogForm(emptyBlog);
      setEditingBlogId(null);
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.error || 'Gagal menyimpan artikel.');
    } finally {
      setSaving(false);
    }
  };

  const deletePortfolio = async (id) => {
    if (!window.confirm('Hapus portofolio ini?')) return;
    try {
      await axios.delete(`${API}/portfolio.php?id=${id}`, { headers: authHeaders });
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.error || 'Gagal menghapus portofolio.');
    }
  };

  const deleteBlog = async (id) => {
    if (!window.confirm('Hapus artikel ini?')) return;
    try {
      await axios.delete(`${API}/blog.php?id=${id}`, { headers: authHeaders });
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.error || 'Gagal menghapus artikel.');
    }
  };

  const submitMenu = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...menuForm,
        order: Number(menuForm.order),
        is_public: Number(menuForm.is_public),
      };

      if (editingMenuId) {
        await axios.put(`${API}/menus.php?id=${editingMenuId}`, payload, { headers: authHeaders });
      } else {
        await axios.post(`${API}/menus.php`, payload, { headers: authHeaders });
      }

      setMenuForm(emptyMenu);
      setEditingMenuId(null);
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.error || 'Gagal menyimpan menu.');
    } finally {
      setSaving(false);
    }
  };

  const deleteMenu = async (id) => {
    if (!window.confirm('Hapus menu ini?')) return;
    try {
      await axios.delete(`${API}/menus.php?id=${id}`, { headers: authHeaders });
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.error || 'Gagal menghapus menu.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <p className="text-muted-foreground">Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <div className="py-20 px-4 sm:px-6 lg:px-8 circuit-bg" data-testid="admin-dashboard-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-sm text-muted-foreground">Kelola konten website dari satu tempat.</p>
          </div>
          <Button variant="outline" onClick={logout}>Logout</Button>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant={activeTab === 'portfolio' ? 'default' : 'outline'} onClick={() => setActiveTab('portfolio')}>
            Portfolio
          </Button>
          <Button variant={activeTab === 'blog' ? 'default' : 'outline'} onClick={() => setActiveTab('blog')}>
            Blog
          </Button>
          <Button variant={activeTab === 'menus' ? 'default' : 'outline'} onClick={() => setActiveTab('menus')}>
            Menus Header
          </Button>
        </div>

        {error ? <p className="text-sm text-red-500">{error}</p> : null}

        {activeTab === 'portfolio' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{editingPortfolioId ? 'Edit Portfolio' : 'Tambah Portfolio'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={submitPortfolio} className="space-y-3">
                  <Input placeholder="Judul ID" value={portfolioForm.title_id} onChange={(e) => setPortfolioForm((v) => ({ ...v, title_id: e.target.value }))} required />
                  <Input placeholder="Title EN" value={portfolioForm.title_en} onChange={(e) => setPortfolioForm((v) => ({ ...v, title_en: e.target.value }))} required />
                  <Textarea placeholder="Deskripsi ID" value={portfolioForm.description_id} onChange={(e) => setPortfolioForm((v) => ({ ...v, description_id: e.target.value }))} />
                  <Textarea placeholder="Description EN" value={portfolioForm.description_en} onChange={(e) => setPortfolioForm((v) => ({ ...v, description_en: e.target.value }))} />
                  <Input placeholder="Kategori" value={portfolioForm.category} onChange={(e) => setPortfolioForm((v) => ({ ...v, category: e.target.value }))} required />
                  <Input placeholder="Image URL" value={portfolioForm.image_url} onChange={(e) => setPortfolioForm((v) => ({ ...v, image_url: e.target.value }))} />
                  <Input placeholder="Client" value={portfolioForm.client} onChange={(e) => setPortfolioForm((v) => ({ ...v, client: e.target.value }))} />
                  <Input type="number" placeholder="Year" value={portfolioForm.year} onChange={(e) => setPortfolioForm((v) => ({ ...v, year: e.target.value }))} />
                  <Input placeholder="Technologies (pisahkan koma)" value={portfolioForm.technologies} onChange={(e) => setPortfolioForm((v) => ({ ...v, technologies: e.target.value }))} />

                  <div className="flex gap-2">
                    <Button type="submit" disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan Portfolio'}</Button>
                    {editingPortfolioId ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingPortfolioId(null);
                          setPortfolioForm(emptyPortfolio);
                        }}
                      >
                        Batal Edit
                      </Button>
                    ) : null}
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Daftar Portfolio</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[700px] overflow-auto">
                {portfolios.map((item) => (
                  <div key={item.id} className="border border-border rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold">{item.title_id}</p>
                        <p className="text-xs text-muted-foreground">{item.client} • {item.year}</p>
                        <Badge className="mt-2" variant="secondary">{item.category}</Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingPortfolioId(item.id);
                            setPortfolioForm(normalizePortfolio(item));
                            setActiveTab('portfolio');
                          }}
                        >
                          Edit
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deletePortfolio(item.id)}>
                          Hapus
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        ) : activeTab === 'menus' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{editingMenuId ? 'Edit Menu' : 'Tambah Menu'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={submitMenu} className="space-y-3">
                  <Input placeholder="Label ID" value={menuForm.label_id} onChange={(e) => setMenuForm((v) => ({ ...v, label_id: e.target.value }))} required />
                  <Input placeholder="Label EN" value={menuForm.label_en} onChange={(e) => setMenuForm((v) => ({ ...v, label_en: e.target.value }))} required />
                  <Input placeholder="Path (e.g. /about)" value={menuForm.path} onChange={(e) => setMenuForm((v) => ({ ...v, path: e.target.value }))} required />
                  <Input type="number" placeholder="Order" value={menuForm.order} onChange={(e) => setMenuForm((v) => ({ ...v, order: e.target.value }))} />
                  
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!menuForm.is_public}
                      onChange={(e) => setMenuForm((v) => ({ ...v, is_public: e.target.checked ? 1 : 0 }))}
                    />
                    Tampilkan di Menu Publik
                  </label>

                  <div className="flex gap-2">
                    <Button type="submit" disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan Menu'}</Button>
                    {editingMenuId ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingMenuId(null);
                          setMenuForm(emptyMenu);
                        }}
                      >
                        Batal Edit
                      </Button>
                    ) : null}
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Daftar Menu</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[700px] overflow-auto">
                {menus.map((item) => (
                  <div key={item.id} className="border border-border rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold">{item.label_id}</p>
                        <p className="text-xs text-muted-foreground">{item.path}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="secondary">Order: {item.order}</Badge>
                          <Badge variant={item.is_public ? 'default' : 'outline'}>{item.is_public ? 'Public' : 'Hidden'}</Badge>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingMenuId(item.id);
                            setMenuForm(item);
                            setActiveTab('menus');
                          }}
                        >
                          Edit
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteMenu(item.id)}>
                          Hapus
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{editingBlogId ? 'Edit Artikel' : 'Tambah Artikel'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={submitBlog} className="space-y-3">
                  <Input placeholder="Judul ID" value={blogForm.title_id} onChange={(e) => setBlogForm((v) => ({ ...v, title_id: e.target.value }))} required />
                  <Input placeholder="Title EN" value={blogForm.title_en} onChange={(e) => setBlogForm((v) => ({ ...v, title_en: e.target.value }))} required />
                  <Input placeholder="Slug" value={blogForm.slug} onChange={(e) => setBlogForm((v) => ({ ...v, slug: e.target.value }))} required />
                  <Input placeholder="Kategori" value={blogForm.category} onChange={(e) => setBlogForm((v) => ({ ...v, category: e.target.value }))} />
                  <Input placeholder="Author" value={blogForm.author} onChange={(e) => setBlogForm((v) => ({ ...v, author: e.target.value }))} />
                  <Input placeholder="Image URL" value={blogForm.image_url} onChange={(e) => setBlogForm((v) => ({ ...v, image_url: e.target.value }))} />
                  <Textarea placeholder="Excerpt ID" value={blogForm.excerpt_id} onChange={(e) => setBlogForm((v) => ({ ...v, excerpt_id: e.target.value }))} />
                  <Textarea placeholder="Excerpt EN" value={blogForm.excerpt_en} onChange={(e) => setBlogForm((v) => ({ ...v, excerpt_en: e.target.value }))} />
                  <Textarea placeholder="Content ID" value={blogForm.content_id} onChange={(e) => setBlogForm((v) => ({ ...v, content_id: e.target.value }))} rows={5} />
                  <Textarea placeholder="Content EN" value={blogForm.content_en} onChange={(e) => setBlogForm((v) => ({ ...v, content_en: e.target.value }))} rows={5} />

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!blogForm.published}
                      onChange={(e) => setBlogForm((v) => ({ ...v, published: e.target.checked }))}
                    />
                    Published
                  </label>

                  <div className="flex gap-2">
                    <Button type="submit" disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan Artikel'}</Button>
                    {editingBlogId ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setEditingBlogId(null);
                          setBlogForm(emptyBlog);
                        }}
                      >
                        Batal Edit
                      </Button>
                    ) : null}
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Daftar Artikel</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[700px] overflow-auto">
                {blogs.map((item) => (
                  <div key={item.id} className="border border-border rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold">{item.title_id}</p>
                        <p className="text-xs text-muted-foreground">{item.slug}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="secondary">{item.category || 'Tanpa kategori'}</Badge>
                          <Badge variant={item.published ? 'default' : 'outline'}>{item.published ? 'Published' : 'Draft'}</Badge>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingBlogId(item.id);
                            setBlogForm({
                              ...emptyBlog,
                              ...item,
                              published: !!item.published,
                            });
                            setActiveTab('blog');
                          }}
                        >
                          Edit
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteBlog(item.id)}>
                          Hapus
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
